import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

export default function ReviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);

  useEffect(() => {
    fetchReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function getAuthHeaders() {
    const token = localStorage.getItem("accessToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async function fetchReview() {
    try {
      const res = await axios.get(`/api/policy-reviews/${id}/`, {
        headers: getAuthHeaders(),
      });
      setReview(res.data);
    } catch (err) {
      console.error("Failed to load review:", err);
      alert("Failed to load review");
    }
  }

  if (!review)
    return (
      <div className="pageWrapper">
        <div className="loadingBox">Loading review details...</div>
      </div>
    );

  return (
    <div className="pageWrapper">
      <div className="detailCard">
        {/* HEADER */}
        <div className="headerRow">
          <div>
            <h1 className="pageTitle">
              {review.policy_name}
              {review.policy_version ? ` (v${review.policy_version})` : ""}
            </h1>

            <div className="metaRow">
              <span>
                <strong>Review Date:</strong>{" "}
                {review.review_date || "-"}
              </span>
              <span>
                <strong>Method:</strong>{" "}
                {review.review_method || "-"}
              </span>
            </div>

            {/* REVIEWERS */}
            <div className="metaRow" style={{ marginTop: 6 }}>
              <span>
                <strong>Reviewers:</strong>{" "}
                {review.reviewers
                  ? review.reviewers
                  : "—"}
              </span>
            </div>
          </div>

          <span
            className={`statusBadge ${
              review.status === "completed"
                ? "statusSuccess"
                : review.status === "draft"
                ? "statusDraft"
                : "statusDefault"
            }`}
          >
            {review.status}
          </span>
        </div>

        {/* SECTIONS */}
        <h3 className="sectionTitle">Review Sections</h3>

        <div className="sectionsGrid">
          {review.sections?.map((s) => (
            <div key={s.section} className="sectionCard">
              <h4 className="sectionName">{s.section.toUpperCase()}</h4>

              <div className="sectionRow">
                <span className="label">Is OK?</span>
                <span
                  className={`pill ${
                    s.is_ok === true
                      ? "pillYes"
                      : s.is_ok === false
                      ? "pillNo"
                      : "pillNA"
                  }`}
                >
                  {s.is_ok === true
                    ? "Yes"
                    : s.is_ok === false
                    ? "No"
                    : "N/A"}
                </span>
              </div>

              <div className="notesBox">
                <strong>Notes</strong>
                <p>{s.notes || "—"}</p>
              </div>
            </div>
          ))}
        </div>

        {/* LINKED ACTIONABLES (SNAPSHOT) */}
        <h3 className="sectionTitle" style={{ marginTop: 30 }}>
          Linked Actionables (Snapshot)
        </h3>

        <table className="modernTable">
          <thead>
            <tr>
              <th>Actionable Number</th>
              <th>Actionable Name</th>
              <th>Status at Review Time</th>
            </tr>
          </thead>
          <tbody>
            {review.actionables?.length ? (
              review.actionables.map((a, i) => (
                <tr key={i}>
                  <td>{a.actionable_number}</td>
                  <td>{a.actionable_name}</td>
                  <td>{a.status}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3">No actionables captured</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* OVERALL COMMENTS */}
        <div className="overallBox">
          <h3 className="sectionTitle">Overall Comments</h3>
          <p>{review.overall_comments || "—"}</p>
        </div>

        {/* ACTIONS */}
        <div className="btnRow">
          <button
            className="backBtn"
            onClick={() => navigate("/policy-reviews/list")}
          >
            ⬅ Back to Reviews
          </button>

          {review.status === "draft" && (
            <button
              className="editBtn"
              onClick={() => navigate(`/policy-reviews/${id}/edit`)}
            >
              Edit Review
            </button>
          )}
        </div>
      </div>

      {/* STYLES */}
      <style>{`
        .pageWrapper {
          padding: 40px;
          background: #f5f7fb;
          min-height: 100vh;
          font-family: Inter, sans-serif;
        }

        .loadingBox {
          background: white;
          padding: 30px;
          border-radius: 16px;
          max-width: 500px;
          margin: auto;
          text-align: center;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }

        .detailCard {
          background: white;
          padding: 30px;
          border-radius: 18px;
          max-width: 1000px;
          margin: auto;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }

        .headerRow {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          gap: 20px;
        }

        .pageTitle {
          font-size: 26px;
          font-weight: 700;
          color: #0f1d60;
        }

        .metaRow {
          display: flex;
          gap: 20px;
          font-size: 14px;
          color: #4b5563;
        }

        .statusBadge {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          text-transform: capitalize;
        }

        .statusSuccess {
          background: #dcfce7;
          color: #166534;
        }

        .statusDraft {
          background: #fef3c7;
          color: #92400e;
        }

        .statusDefault {
          background: #e5e7eb;
          color: #374151;
        }

        .sectionTitle {
          margin-top: 25px;
          margin-bottom: 12px;
          font-size: 20px;
          font-weight: 600;
          color: #102a71;
        }

        .sectionsGrid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .sectionCard {
          background: #f9fafc;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 18px;
        }

        .sectionName {
          font-weight: 700;
          margin-bottom: 10px;
        }

        .pill {
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
        }

        .pillYes { background: #dcfce7; color: #166534; }
        .pillNo { background: #fee2e2; color: #991b1b; }
        .pillNA { background: #e5e7eb; color: #374151; }

        .modernTable {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
        }

        .modernTable th,
        .modernTable td {
          border: 1px solid #e5e7eb;
          padding: 10px;
        }

        .modernTable th {
          background: linear-gradient(135deg, #102a71, #2053c3);
          color: white;
        }

        .overallBox {
          margin-top: 30px;
          padding: 18px;
          background: #f9fafc;
          border-radius: 14px;
          border: 1px solid #e5e7eb;
        }

        .btnRow {
          margin-top: 30px;
          display: flex;
          gap: 12px;
        }

        .backBtn {
          background: #4b5563;
          color: white;
          padding: 12px 18px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
        }

        .editBtn {
          background: #2563eb;
          color: white;
          padding: 12px 18px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
